import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { fetchBookingPipeline } from "../../../API_Service/Dashboard_Slice";
import { useDashboardSignalR } from "../../..//Common/useDashboardSignalR";
import { DashboardCard, ChartSkeleton, InlineAlert } from "../../../Common/UIComponents";
import { CHART_PALETTE, dataLabelStyle, tooltipStyleBase } from "../../../Common/chartTheme";

const STAGES = [
  { key: "requested", label: "Requested", color: CHART_PALETTE[2], icon: "➜" },
  {
    key: "pendingApproval",
    label: "Pending Approval",
    color: CHART_PALETTE[3],
    icon: "⏱",
  },
  { key: "approved", label: "Approved", color: CHART_PALETTE[0], icon: "✔" },
  { key: "running", label: "Running", color: CHART_PALETTE[4], icon: "▶" },
  { key: "completed", label: "Completed", color: CHART_PALETTE[1], icon: "🏁" },
];

const BookingPipelineChart = ({ companyId }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    dispatch(fetchBookingPipeline({ companyId }))
      .unwrap()
      .then(setData)
      .catch((err) => setError(err?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [dispatch, companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useDashboardSignalR(loadData);

  const total = data?.requested || 1;
  const conversionRate = data?.conversionRate
    ? parseFloat(data.conversionRate).toFixed(1)
    : "0.0";

  const chartData = useMemo(
    () => ({
      series: STAGES.map((s) => data?.[s.key] ?? 0),
      labels: STAGES.map((s) => s.label),
      colors: STAGES.map((s) => s.color),
    }),
    [data],
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
          export: {
            csv: {
              filename: "Booking_Pipeline",
              columnDelimiter: ",",
              headerCategory: "Stage",
              headerValue: "Bookings",
            },
            svg: { filename: "Booking_Pipeline" },
            png: { filename: "Booking_Pipeline" },
          },
        },
        fontFamily: "inherit",
      },
      colors: chartData.colors,
      labels: chartData.labels,
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "12px",
                color: "#6B7280",
              },
              value: {
                show: true,
                fontSize: "16px",
                fontWeight: 700,
                color: "#1F2937",
              },
              total: {
                show: true,
                label: "Total Bookings",
                fontSize: "12px",
                color: "#6B7280",
                formatter: () => total.toLocaleString(),
              },
            },
          },
        },
      },
      legend: {
        position: "bottom",
        labels: {
          colors: "#374151",
          useSeriesColors: false,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (_, { seriesIndex }) => {
          const actualValue = chartData.series[seriesIndex];
          const pct = ((actualValue / total) * 100).toFixed(1);
          return `${pct}%`;
        },
        style: { ...dataLabelStyle, colors: ["#ffffff"] },
      },
      tooltip: {
        enabled: true,
        theme: "light",
        style: tooltipStyleBase,
        y: {
          formatter: (val) => {
            const pct = ((val / total) * 100).toFixed(1);
            return `${val.toLocaleString()} bookings (${pct}%)`;
          },
        },
      },
    }),
    [chartData, total],
  );

  if (loading)
    return (
      <DashboardCard icon="donut_large" title="Booking Pipeline">
        <ChartSkeleton height={256} />
      </DashboardCard>
    );

  if (error)
    return (
      <DashboardCard icon="donut_large" title="Booking Pipeline">
        <InlineAlert type="error" message={error} />
      </DashboardCard>
    );

  return (
    <DashboardCard icon="donut_large" title="Booking Pipeline">
      <div>
        {/* Conversion Summary - Top */}
        <div className="mb-6">
          <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-[#08757C] to-[#0a9a99] p-5 border border-[#08757C]/30">
            <div className="absolute -right-20 -top-20 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -left-20 -bottom-20 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <p className="text-white/80 text-sm font-medium mb-2">Overall Conversion Rate</p>
              <div className="flex items-baseline gap-3">
                <p className="text-4xl font-bold text-white">{conversionRate}%</p>
                <p className="text-white/70 text-sm">
                  {(data?.completed ?? 0).toLocaleString()} of{" "}
                  {(data?.requested ?? 0).toLocaleString()} completed
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Donut Chart with Breakdown List */}
        {/* Breakdown is a single vertical list (not a 2-column grid) so it
            stays narrow — that's what leaves the fixed-size chart enough
            room in this row without the two overlapping, even now that
            Booking Pipeline shares a row with Laboratory Utilization
            Ranking above `lg`. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chart - Left */}
          <div className="flex justify-center">
            <div className="chart-fade-in bg-slate-50 rounded-lg p-4 border border-slate-200/60 w-full overflow-visible">
              <div className="overflow-visible flex justify-center">
                <Chart
                  options={chartOptions}
                  series={chartData.series}
                  type="donut"
                  height={340}
                  width={340}
                />
              </div>
            </div>
          </div>

          {/* Breakdown List - Right (single vertical line) */}
          <div className="flex items-center">
            <div className="bg-slate-50 rounded-lg border border-slate-200/60 p-4 w-full">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4 px-1">Stage Breakdown</p>
              <div className="grid grid-cols-1 gap-2">
                {STAGES.map((stage) => {
                  const val = data?.[stage.key] ?? 0;
                  const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
                  return (
                    <div
                      key={stage.key}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-white transition-colors duration-200 bg-white border border-slate-100"
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: stage.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700">
                          {stage.label}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-slate-900">
                          {pct}%
                        </p>
                        <p className="text-xs text-slate-500">
                          {val.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};

export default BookingPipelineChart;